import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  IAppSlice,
  IEditSlice,
  IHomeSlice,
  IPatternSlice,
  ISearchSlice,
  IUserSlice,
  INotificationSlice,
  IPresenceSlice,
  IResetSlice,
} from '@/types'
import {
  createAppSlice,
  createHomeSlice,
  createUserSlice,
  createEditSlice,
  createSearchSlice,
  createPatternSlice,
  createNotificationSlice,
  createPresenceSlice,
  resetAllSlices,
} from './slices'

type CombinedSlice = IAppSlice &
  IHomeSlice &
  IUserSlice &
  IEditSlice &
  ISearchSlice &
  IPatternSlice &
  INotificationSlice &
  IPresenceSlice &
  IResetSlice

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Zustand's default persist merge is a shallow `{ ...current, ...persisted }`
 * at the top level only. Every slice nests its actual data one level down
 * (`user`, `home`, `pattern`, ...) alongside its action functions, so that
 * shallow merge replaces e.g. `state.user` wholesale with whatever's in an
 * older localStorage snapshot — any field added to a slice's shape *after*
 * a browser already persisted a session (like `sentConnectionRequests`)
 * silently comes back `undefined` instead of falling back to the slice's
 * current-code default. Recurse one extra level so newly-added fields keep
 * their fresh default when missing from the persisted blob, while fields
 * actually present in the persisted blob still win.
 */
function mergeWithDefaults<T>(current: T, persisted: unknown): T {
  if (!isPlainObject(current) || !isPlainObject(persisted)) {
    return (persisted ?? current) as T
  }
  const merged: Record<string, unknown> = { ...current }
  for (const key of Object.keys(persisted)) {
    const persistedValue = persisted[key]
    const currentValue = merged[key]
    merged[key] =
      isPlainObject(currentValue) && isPlainObject(persistedValue)
        ? mergeWithDefaults(currentValue, persistedValue)
        : persistedValue !== undefined
          ? persistedValue
          : currentValue
  }
  return merged as T
}

export const useAppStore = create<CombinedSlice, [['zustand/persist', CombinedSlice]]>(
  persist(
    (set) => ({
      ...createAppSlice(set),
      ...createHomeSlice(set),
      ...createUserSlice(set),
      ...createEditSlice(set),
      ...createSearchSlice(set),
      ...createPatternSlice(set),
      ...createNotificationSlice(set),
      ...createPresenceSlice(set),
      ...resetAllSlices(set),
    }),
    {
      name: 'appStore',
      merge: (persistedState, currentState) => mergeWithDefaults(currentState, persistedState),
    },
  ),
)

export const clearAllStores = () => {
  const data = window.localStorage.getItem('appStore')
  console.log('Data: ', data)
  if (data) {
    window.localStorage.removeItem('appStore')
    useAppStore.persist.clearStorage()
    window.location.reload()
  }
}

export default useAppStore
